import dayjs from 'dayjs';

// 🎯 COSTANTE CONDIVISA: Periodo per considerare un elemento "nuovo" 
// Usata sia per il badge NEW che per il filtro showNews
export const NEW_ITEM_THRESHOLD = { amount: 1, unit: 'month' };

// Funzione per normalizzare il testo per la ricerca (rimuove accenti e converte in minuscolo)
const normalizeText = (text) => {
    if (!text) return '';
    return text
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};

// Immagine placeholder per errori CORS e 404 - usa un'immagine più generica
const createPlaceholderImage = (width = 400, height = 400) => {
    return 'data:image/svg+xml;base64,' + btoa(`
<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <rect width="${width}" height="${height}" fill="#f5f5f5" stroke="#d9d9d9" stroke-width="2"/>
  <g transform="translate(${width / 2}, ${height / 2})">
    <text x="0" y="-10" font-family="Arial, sans-serif" font-size="${Math.min(width, height) * 0.08}" fill="#999" text-anchor="middle">No Image</text>
    <text x="0" y="15" font-family="Arial, sans-serif" font-size="${Math.min(width, height) * 0.06}" fill="#999" text-anchor="middle">Available</text>
  </g>
</svg>
`);
};

const DEFAULT_PLACEHOLDER = createPlaceholderImage();

// Funzione per rimuovere lo sfondo bianco da un'immagine (versione sincrona restaurata)
export const removeWhiteBackground = (imageUrl, id, processedImages, setProcessedImages) => {
    if (!imageUrl) return null;
    if (processedImages[id]) return processedImages[id];

    try {
        // Crea un elemento immagine per caricare l'URL
        const img = new Image();
        img.crossOrigin = "Anonymous";

        img.onload = () => {
            try {
                // Crea un canvas per elaborare l'immagine
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Imposta le dimensioni del canvas
                canvas.width = img.width;
                canvas.height = img.height;

                // Disegna l'immagine nel canvas
                ctx.drawImage(img, 0, 0);

                // Ottieni i dati dei pixel dell'immagine
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;

                // Rimuovi lo sfondo bianco rendendo trasparenti i pixel bianchi
                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];

                    // Se il pixel è vicino al bianco, rendilo trasparente
                    if (r > 240 && g > 240 && b > 240) {
                        data[i + 3] = 0; // Imposta il canale alpha a 0 (trasparente)
                    }
                }

                // Aggiorna l'immagine nel canvas
                ctx.putImageData(imageData, 0, 0);

                // Converti il canvas in un URL di dati
                const processedImageUrl = canvas.toDataURL('image/png');

                // Aggiorna lo stato delle immagini elaborate
                setProcessedImages(prev => ({
                    ...prev,
                    [id]: processedImageUrl
                }));
            } catch (canvasError) {
                console.warn(`Canvas processing failed for image ${imageUrl}:`, canvasError.message);
                // In caso di errore del canvas, usa l'immagine originale
                setProcessedImages(prev => ({
                    ...prev,
                    [id]: imageUrl
                }));
            }
        };

        img.onerror = (error) => {
            console.warn(`Failed to load image ${imageUrl}:`, error);
            // In caso di errore di caricamento, usa il placeholder
            setProcessedImages(prev => ({
                ...prev,
                [id]: DEFAULT_PLACEHOLDER
            }));
        };

        img.src = imageUrl;

        // Restituisci l'URL originale immediatamente, l'elaborazione avverrà in background
        return imageUrl;
    } catch (error) {
        console.warn(`Error processing image ${imageUrl}:`, error.message);
        return imageUrl; // Restituisci l'URL originale in caso di errore
    }
};

// Funzione per filtrare i prodotti in base al termine di ricerca
export const filterProducts = (products, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
        return products;
    }

    const normalized = normalizeText(searchTerm.trim());

    return products.filter(product => {
        // Ricerca nel nome del modello
        if (normalizeText(product.modelName).includes(normalized)) {
            return true;
        }

        // Ricerca nel nome del brand
        if (normalizeText(product.brandName).includes(normalized)) {
            return true;
        }

        // Ricerca nei codici del prodotto (SKU, EAN, UPC)
        if (
            (product.sku && normalizeText(product.sku).includes(normalized)) ||
            (product.ean && normalizeText(product.ean).includes(normalized)) ||
            (product.upc && normalizeText(product.upc).includes(normalized))
        ) {
            return true;
        }

        // Ricerca nelle varianti (se presenti)
        if (product.variants && Array.isArray(product.variants)) {
            return product.variants.some(variant => {
                return (
                    // Nome della variante
                    (variant.glassesName && normalizeText(variant.glassesName).includes(normalized)) ||
                    // SKU della variante
                    (variant.skuModel && normalizeText(variant.skuModel).includes(normalized)) ||
                    // EAN della variante
                    (variant.ean && normalizeText(variant.ean).includes(normalized)) ||
                    // UPC della variante
                    (variant.upc && normalizeText(variant.upc).includes(normalized))
                );
            });
        }

        return false;
    });
};

// Ordina le varianti per stato
export const sortVariantsByStatus = (variants) => {
    if (!variants || !Array.isArray(variants)) return [];

    return [...variants].sort((a, b) => {
        const order = ['In Publication', 'Client Rev.', 'Spaarkly Rev.', 'Modelist Rev.', 'Incomplete'];
        return order.indexOf(a.status) - order.indexOf(b.status);
    });
};

/**
 * Verifica se un prodotto o variante è stato pubblicato di recente (entro l'ultimo mese)
 * @param {Object} item - Il prodotto o la variante da verificare
 * @returns {boolean} - True se l'elemento è nuovo (pubblicato nell'ultimo mese)
 */
export const isNewItem = (item) => {
    if (!item) {
        return false;
    }

    // Per i prodotti cerchiamo lastPublishedInARSLibrary, per le varianti publishedInARSLibrary
    const publishedTimestamp = item.lastPublishedInARSLibrary || item.publishedInARSLibrary;

    if (!publishedTimestamp) {
        return false;
    }

    try {
        // Converte il timestamp in dayjs object
        let publishedDate;

        // Gestisce diversi formati di timestamp
        if (publishedTimestamp.toDate) {
            // Firestore Timestamp
            publishedDate = dayjs(publishedTimestamp.toDate());
        } else {
            // Timestamp normale o stringa
            publishedDate = dayjs(publishedTimestamp);
        }

        // Verifica se la data è valida
        if (!publishedDate.isValid()) {
            return false;
        }

        // Calcola la data soglia usando la costante condivisa
        const thresholdDate = dayjs().subtract(NEW_ITEM_THRESHOLD.amount, NEW_ITEM_THRESHOLD.unit);

        // Verifica se la data di pubblicazione è dopo la soglia
        const isRecent = publishedDate.isAfter(thresholdDate);

        return isRecent;
    } catch (error) {
        console.error('Error checking if item is new:', error);
        return false;
    }
}; 